import React from 'react';
import { Link } from 'react-router-dom';
import { Navbar } from '../components/navbar'; // ⚠️ ADJUST: point this at wherever navbar.jsx actually lives
import DownloadButton from '../components/whitepaper/DownloadButton';
import {
  Icon,
  Figure2Dvs4D,
  ConvergenceDiagram,
  StatBand,
  PullQuote,
  Point,
} from '../components/whitepaper/Visuals';

const PDF_URL = '/whitepapers/from-dashboards-to-world-models.pdf';

const P = ({ children }) => (
  <p className="text-gray-600 leading-relaxed mb-5">{children}</p>
);

const Section = ({ num, eyebrow, title, children }) => (
  <section className="mb-20">
    <p className="font-mono text-xs uppercase tracking-[0.25em] text-gray-400 mb-3">
      {num} · {eyebrow}
    </p>
    <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-gray-900 mb-6">
      {title}
    </h2>
    {children}
  </section>
);

export default function WorldModelsPaper() {
  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <main className="max-w-4xl mx-auto px-4 py-8">
        <article className="bg-white rounded-lg shadow-sm p-6 sm:p-10">
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-gray-400 mb-5">
            Smarlytics Whitepaper
          </p>
          <h1 className="text-3xl sm:text-4xl font-black tracking-tight text-gray-900 leading-tight">
            From Dashboards to World Models
          </h1>
          <p className="mt-6 text-lg text-gray-500 leading-relaxed max-w-3xl">
            Why enterprise insight is moving from 2D charts to 4D spaces: the three
            bottlenecks that held it back for thirty years, and the convergence that
            finally broke them.
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-4 text-xs text-gray-400">
            <span className="inline-flex items-center gap-1.5">
              <Icon className="w-3.5 h-3.5">
                <circle cx="12" cy="12" r="10" />
                <polyline points="12 6 12 12 16 14" />
              </Icon>
              3 min read
            </span>
            <span className="w-px h-3 bg-gray-200" />
            <span>Operations · AI · Spatial Intelligence</span>
          </div>

          <Figure2Dvs4D />

          <div className="mt-16">
            <Section num="01" eyebrow="The Thesis" title="Insight is leaving the dashboard">
              <P>
                For thirty years, the dashboard has been the terminal of enterprise
                truth. It is a 2D artifact: flat charts, stacked against time, asking
                the reader to mentally reconstruct the physical reality the data came
                from. A dip in conversion is a red cell. A bottleneck is a bar. The
                reader, not the tool, does the work of turning numbers back into a
                place, a moment, a cause.
              </P>
              <P>
                That reconstruction is where insight dies. Operations do not happen in
                2D. They happen in spaces, over time: a shopper moving past an endcap,
                a pallet waiting two hours in a receiving bay, a queue forming at
                3&nbsp;PM every Saturday. We call this the fourth dimension, space and
                time fused, and we believe the next decade of enterprise analytics
                belongs to whoever can put operational data back into the world it was
                born in              </P>
              <PullQuote>
                This is not visualization for its own sake. It is the difference between
                reading a transcript of a match and watching the replay. One tells you
                the score. The other shows you why.
              </PullQuote>
            </Section>
          </div>

          <Section num="02" eyebrow="Why It Never Happened" title="Three bottlenecks held 4D analytics back">
            <P>
              The idea of spatially-grounded analytics is not new. What was new,
              very recently, was making it survivable at enterprise scale. Three
              structural bottlenecks stood in the way:
            </P>
            <div className="flex flex-col gap-6 my-8">
              <Point
                title="Spatial data lived outside the data platform"
                icon={
                  <Icon>
                    <path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z" />
                    <path d="M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2" />
                    <path d="M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2" />
                    <path d="M10 6h4" />
                    <path d="M10 10h4" />
                    <path d="M10 14h4" />
                    <path d="M10 18h4" />
                  </Icon>
                }
              >
                3D models, floorplans, sensor coordinates and IoT telemetry lived in CAD
                files, BIM systems and vendor silos, while business metrics lived in the
                lakehouse. The two never met, because joining them meant bespoke ETL
                projects that cost more than the insight was worth and rotted the moment
                a floor changed.
              </Point>
              <Point
                title="Rendering 4 was a specialist discipline"
                icon={
                  <Icon>
                    <polyline points="16 18 22 12 16 6" />
                    <polyline points="8 6 2 12 8 18" />
                  </Icon>
                }
              >
                Putting a metric onto a moving 3D scene in real time required game-engine
                engineers, custom WebGL pipelines and six-figure prototypes. Every
                deployment was a one-off. Nothing generalized. Enterprises rightly
                concluded it was a demo technology, not a platform.
              </Point>
              <Point
                title="The past could be charted, but not replayed"
                icon={
                  <Icon>
                    <circle cx="12" cy="12" r="10" />
                    <polyline points="12 6 12 12 16 14" />
                  </Icon>
                }
              >
                Time-series tools could graph what happened; nothing could re-stage it.
                You could see that throughput dropped at 14:00, but not watch the
                receiving dock fill up, the forklifts queue, and the cascade unfold.
                Playback of operational history, the way sports teams review game film,
                simply did not exist for the enterprise.
              </Point>
            </div>
          </Section>

          <Section num="03" eyebrow="What Changed" title="World models complete the stack">
            <P>
              Each bottleneck has independently collapsed in the last three years.
              Spatial data now lands in lakehouse-native formats; GPU rendering has
              commoditized in the browser; and the decisive piece, generative world
              models, has matured from research demos to systems that can represent
              physical environments grounded in real data.
            </P>
            <ConvergenceDiagram />
            <P>
              A world model is a learnable representation of an environment: it holds
              the geometry of a space, the behavior of the things inside it, and the
              causal texture that connects them. When grounded in your operational
              data, every sensor reading, transaction and event across your real-time
              estate, it becomes something no dashboard can be: a live, queryable,
              replayable replica of your operations.
            </P>
            <P>
              Grounding is the word that matters. Ungrounded generative models
              hallucinate; grounded ones reflect. Our world model does not imagine your
              stores, warehouses and floors. It reconstructs them from your data, which
              means every replay of the past and every simulation of the future is
              accountable to what actually happened.
            </P>
          </Section>

          <Section num="04" eyebrow="What This Unlocks" title="From reading charts to running scenarios">
            <div className="flex flex-col gap-6 my-8">
              <Point
                title="Replay: time travel for operations"
                icon={<Icon><polygon points="6 3 20 12 6 21 6 3" /></Icon>}
              >
                Re-stage any shift, any hour, any incident as it actually occurred, with
                KPIs pinned to the exact space and moment. Root-cause analysis stops
                being a forensic guessing exercise.
              </Point>
              <Point
                title="Simulate: test before you commit"
                icon={
                  <Icon>
                    <circle cx="12" cy="12" r="3" />
                    <path d="M12 2v4" />
                    <path d="M12 18v4" />
                    <path d="m4.9 4.9 2.8 2.8" />
                    <path d="m16.3 16.3 2.8 2.8" />
                    <path d="M2 12h4" />
                    <path d="M18 12h4" />
                    <path d="m4.9 19.1 2.8-2.8" />
                    <path d="m16.3 7.7 2.8-2.8" />
                  </Icon>
                }
              >
                Ask the world model what happens if you move the endcap, add a till, or
                change a pick path, and see the projected KPI impact on the actual space
                before spending a dollar on the change.
              </Point>
              <Point
                title="Share: alignment at the speed of video"
                icon={
                  <Icon>
                    <path d="m22 8-6 4 6 4V8Z" />
                    <rect x="2" y="6" width="14" height="12" rx="2" />
                  </Icon>
                }
              >
                Turn any finding into a short, spatial video brief that every
                stakeholder, from the floor to the boardroom, understands in thirty
                seconds, no analytics training required.
              </Point>
              <Point
                title="Generalize: one model, every space"
                icon={
                  <Icon>
                    <path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z" />
                    <path d="m3.3 7 8.7 5 8.7-5" />
                    <path d="M12 22V12" />
                  </Icon>
                }
              >
                Runs native on your existing lakehouse, Databricks, Snowflake, so there
                is no new stack to buy, no data to migrate, no pipeline to build. The
                fourth dimension arrives as a layer, not a migration.
              </Point>
            </div>
          </Section>

          <Section num="05" eyebrow="The Claim" title="The dashboard is not dying, it is being absorbed">
            <P>
              We do not believe 2D analytics disappears. We believe it becomes a view
              inside something larger: a grounded world model where any chart, any
              metric, any KPI has an address in space and time. The organizations that
              make this shift first will not just see their operations more clearly.
              They will rehearse their decisions before making them.
            </P>
            <P>
              The bottlenecks that made this impossible for thirty years have fallen
              within thirty months. The window between "possible" and "standard" is
              where competitive advantage lives. This paper is an invitation to stand
              on the right side of it.
            </P>
            <StatBand />
          </Section>

          <div className="border border-gray-200 bg-gray-50 rounded-xl p-8 sm:p-10 text-center">
            <h3 className="text-xl font-bold tracking-tight text-gray-900">
              See it running on your data
            </h3>
            <p className="mt-3 text-sm text-gray-500 max-w-md mx-auto leading-relaxed">
              We deploy forward: embedded with your team, on your lakehouse, live in
              weeks. Request early access to a pilot.
            </p>
            <div className="mt-6 flex flex-col sm:flex-row justify-center gap-3">
              <Link
                to="/#signup"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg bg-gray-900 text-white text-sm font-semibold hover:bg-gray-700 transition-colors"
              >
                Request early access
                <Icon className="w-4 h-4"><path d="M5 12h14" /><path d="m12 5 7 7-7 7" /></Icon>
              </Link>
            </div>
          </div>
        </article>

        <footer className="mt-8 text-center text-gray-500 text-sm">
          <p>Last updated: August 6, 2026</p>
        </footer>
      </main>
    </div>
  );
}


export function WorldModelsThesis() {
  return <WorldModelsPaper />;
}